/**
 * ч.757 (рішення власника): попап «Податок» у платіжці зі сплати податків —
 * податкові рахунки з картки організації (вкладка «Податкові рахунки»:
 * 3 системні ПДФО/ВЗ/ЄСВ з ЗП + користувацькі) з можливістю створити новий
 * (зберігається у картку організації як користувацький рядок) і одразу обрати.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orgs, type Organization, type OrgTaxAccount } from '@/api/catalogs';
import { DEFAULT_TAX_PURPOSE_TEMPLATE } from '@/shared/taxPurposeTemplate';
import TchTable from '@/shared/components/TchTable';

export interface OrgTaxItem {
  key: string;
  name: string;
  recipient_name: string | null;
  recipient_edrpou: string | null;
  recipient_iban: string | null;
  purpose_template?: string | null;
  tax_type?: 'PDFO' | 'WZ' | 'ESV' | null;
  // ч.806: ПДФО відокремленого підрозділу (рахунок місцевого бюджету місця).
  activity_place_id?: string | null;
}

/** Усі податкові рахунки організації: системні (ЗП) + користувацькі. */
export function orgTaxItems(org: Organization | undefined): OrgTaxItem[] {
  if (!org) return [];
  const tpl = org.tax_purpose_templates ?? {};
  const sys: OrgTaxItem[] = [
    { key: 'pdfo', name: 'ПДФО (ЗП)', tax_type: 'PDFO' as const, recipient_name: org.tax_pdfo_recipient_name,
      recipient_edrpou: org.tax_pdfo_recipient_edrpou, recipient_iban: org.tax_pdfo_recipient_iban,
      purpose_template: tpl.pdfo ?? null },
    { key: 'wz', name: 'Військовий збір (ЗП)', tax_type: 'WZ' as const, recipient_name: org.tax_wz_recipient_name,
      recipient_edrpou: org.tax_wz_recipient_edrpou, recipient_iban: org.tax_wz_recipient_iban,
      purpose_template: tpl.wz ?? null },
    { key: 'esv', name: 'ЄСВ (ЗП)', tax_type: 'ESV' as const, recipient_name: org.tax_esv_recipient_name,
      recipient_edrpou: org.tax_esv_recipient_edrpou, recipient_iban: org.tax_esv_recipient_iban,
      purpose_template: tpl.esv ?? null },
  ].filter((x) => x.recipient_name || x.recipient_edrpou || x.recipient_iban);
  // ч.806: ПДФО по місцях діяльності (відокремлені підрозділи).
  const places: OrgTaxItem[] = (org.activity_places ?? []).map((p) => ({
    key: `p-${p.id}`, name: `ПДФО · ${p.name}`, tax_type: 'PDFO' as const,
    recipient_name: p.pdfo_recipient_name, recipient_edrpou: p.pdfo_recipient_edrpou,
    recipient_iban: p.pdfo_recipient_iban,
    purpose_template: p.pdfo_purpose_template ?? tpl.pdfo ?? null,
    activity_place_id: p.id ?? null,
  }));
  const extra = (org.tax_accounts_extra ?? []).map((x, i) => ({
    key: `x${i}`, name: x.name, recipient_name: x.recipient_name,
    recipient_edrpou: x.recipient_edrpou, recipient_iban: x.recipient_iban,
    purpose_template: x.purpose_template ?? null, tax_type: null,
  }));
  return [...sys, ...places, ...extra];
}

export default function OrgTaxAccountPicker(props: {
  organization: Organization;
  onPick: (item: OrgTaxItem) => void;
  onClose: () => void;
}) {
  const { organization, onPick, onClose } = props;
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [recipient, setRecipient] = useState('');
  const [code, setCode] = useState('');
  const [iban, setIban] = useState('');
  const [tplText, setTplText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const items = orgTaxItems(organization);

  const create = useMutation({
    mutationFn: () => {
      const row: OrgTaxAccount = {
        name: name.trim(), recipient_name: recipient.trim() || null,
        recipient_edrpou: code.trim() || null,
        recipient_iban: iban.replace(/\s+/g, '').toUpperCase() || null,
        purpose_template: tplText.trim() || null,
      };
      return orgs.update(organization.id, {
        tax_accounts_extra: [...(organization.tax_accounts_extra ?? []), row],
      }).then(() => row);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      onPick({ key: 'new', name: row.name, recipient_name: row.recipient_name,
        recipient_edrpou: row.recipient_edrpou, recipient_iban: row.recipient_iban,
        purpose_template: row.purpose_template ?? null, tax_type: null });
      onClose();
    },
    onError: (e) => setErr((e as { response?: { data?: { detail?: string } } })
      ?.response?.data?.detail ?? 'Не вдалося зберегти податковий рахунок у картку організації'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 60,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div className="card" style={{ width: 760, maxWidth: '95vw', padding: '1rem' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Податкові рахунки організації</h3>
          <button type="button" style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        {items.length === 0 && (
          <p style={{ color: 'var(--muted)', margin: '0 0 0.5rem' }}>
            У картці організації податкових рахунків ще немає — додайте нижче.
          </p>
        )}
        {items.length > 0 && (
          <TchTable style={{ marginBottom: '0.75rem' }}>
            <thead>
              <tr><th>Податок</th><th>Отримувач</th><th style={{ width: 110 }}>Код</th><th>IBAN</th><th style={{ width: 80 }}></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.key}>
                  <td>{it.name}</td>
                  <td>{it.recipient_name ?? '—'}</td>
                  <td>{it.recipient_edrpou ?? '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{it.recipient_iban ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="primary"
                      onClick={() => { onPick(it); onClose(); }}>обрати</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 0.8fr 1.4fr auto', gap: '0.5rem', alignItems: 'end' }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Податок *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Єдиний податок" />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Отримувач</label>
            <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="ГУ ДПС у …" />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Код</label>
            <input value={code} maxLength={10} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>IBAN</label>
            <input value={iban} maxLength={34} placeholder="UA…" onChange={(e) => setIban(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0, gridColumn: '1 / -1' }}>
            <label>Призначення платежу (шаблон)</label>
            <input value={tplText} maxLength={420} placeholder={DEFAULT_TAX_PURPOSE_TEMPLATE}
              title="Змінні: {edrpou} {tax} {tax_name} {period} {mm} {yyyy} {payout} {org}"
              onChange={(e) => setTplText(e.target.value)} />
          </div>
          <button type="button" className="primary" disabled={!name.trim() || create.isPending}
            title="Зберегти у картку організації (вкладка «Податкові рахунки») і підставити"
            onClick={() => { setErr(null); create.mutate(); }}>
            додати і обрати
          </button>
        </div>
        {err && <div className="error" style={{ marginTop: '0.5rem' }}>{err}</div>}
      </div>
    </div>
  );
}
